import { supabase } from '@/integrations/supabase/client';
import { slugify } from '@/utils/slugify';
import { SeoMetadataInput } from '@/types/seo';
import { NOTE_AGREGEE_GOOGLE } from '@/constants/avisGoogle';
import { ADRESSE_ENTREPRISE, GEO_ENTREPRISE } from '@/constants/entreprise';

/**
 * Synchronise les event_categories actives vers les métadonnées SEO
 * Crée une page SEO pour chaque évènement personnalisable
 */
export const syncEventCategoriesToSeo = async () => {
  try {
    console.log('Synchronisation des évènements vers SEO...');

    // Récupérer toutes les catégories d'évènements actives
    const { data: eventCategories, error: eventsError } = await supabase
      .from('event_categories')
      .select('*')
      .eq('is_active', true)
      .order('order');

    if (eventsError) {
      console.error('Erreur lors de la récupération des évènements:', eventsError);
      throw eventsError;
    }

    if (!eventCategories || eventCategories.length === 0) {
      console.log('Aucune catégorie d\'évènement à synchroniser');
      return;
    }

    console.log(`${eventCategories.length} catégories d'évènements trouvées`);

    // Préparer les métadonnées SEO pour chaque évènement
    const seoMetadataList: SeoMetadataInput[] = eventCategories.map((event) => {
      // Déterminer le chemin selon le type d'évènement
      const isProfessionnel = event.type_categorie === 'professionnel';
      let pagePath: string;
      let eventSlug: string;
      
      if (isProfessionnel) {
        // Pour les évènements professionnels: /professionnels/evenements/{slug}
        eventSlug = event.slug || slugify(event.title);
        pagePath = `/professionnels/evenements/${eventSlug}`;
      } else {
        // Pour les évènements particuliers: /evenements/{slug}-{uuid}/personnaliser
        eventSlug = `${slugify(event.title)}-${event.id}`;
        pagePath = `/evenements/${eventSlug}/personnaliser`;
      }
      
      // Générer des mots-clés pertinents et variés avec Maison Eridia
      const eventType = event.type_categorie === 'particulier' ? 'particuliers' : 'professionnels';
      
      // Mots-clés longue traîne spécifiques selon le type d'évènement
      const longTailKeywords = event.type_categorie === 'particulier' ? [
        `organisation d'anniversaire pour adulte à Paris`,
        `agence évènementielle mariage haut de gamme Île-de-France`,
        `décoration de table pour 130 invités en région parisienne`,
        `demande en mariage sur-mesure Paris Île-de-France`,
        `Saint-Valentin élégante Paris Île-de-France`,
        `célébration privée élégante région parisienne`,
        `réception haut de gamme pour 130 invités Île-de-France`,
        `décorations florales haut de gamme mariage Paris`,
        `location machine à barbe à papa pour anniversaire Paris`,
        `location pop-corn machine évènement privé Île-de-France`,
        `prestation décoration évènement familial Paris`,
        `décorateur évènementiel table invité haut de gamme Île-de-France`,
        `table d'honneur mariage luxe Paris`,
        `offre décoration anniversaire VIP Île-de-France`,
        `organiser une fête privatisée élégante Paris`,
        `concept évènementiel intime pour adultes Île-de-France`,
        `décoration thème automne table invités Paris`,
        `animation évènement privée premium Paris`,
        // Nouveaux mots-clés longue traîne ultra-ciblés
        `décoration de table pour mariage 120 invités Paris 11ᵉ`,
        `location mobilier réception 130 personnes Boulogne-Billancourt`,
        `agence organisation anniversaire adulte 50 personnes Neuilly-sur-Seine`,
        `décoration anniversaire signature luxe 40 invités Versailles`,
        `location machine à barbe à papa anniversaire adulte 30 invités Saint-Denis`,
        `scénographie célébration privée bohème chic Montreuil Seine-Saint-Denis`,
        `organisation réception privée 100 invités Hauts-de-Seine`,
        `location pop-corn machine 80 invités Nanterre`,
        `décoration table d'honneur mariage luxe 12 personnes 3ᵉ arrondissement Paris`,
        `table des invités mariage 130 personnes nappage premium Fontainebleau`,
        `organisation réception intimiste 70 invités Paris 16ᵉ`,
        `location accessoires décoration thème automne 100 invités Saint-Maur-des-Fossés`,
        `agence décoration table anniversaire adulte 40 invités Montreuil`,
        `location mobilier évènementiel mariage 130 personnes Versailles`,
        `décorations de table personnalisées noms des invités mariage Paris 4ᵉ`,
        `location décor sur-mesure célébration privée 35 invités Saint-Denis`,
        `organisation demande en mariage intimiste Paris 10ᵉ`,
        `location machine à barbe à papa mariage 130 invités Boulogne-Billancourt`,
        `décor centre de table lourd mariage 11 tables Versailles`,
        `location mobilier vintage réception 130 invités Saint-Germain-en-Laye`,
        `agence organisation demande en mariage rooftop Paris 8ᵉ`,
        `décoration table d'honneur mariage couture Paris 7ᵉ`,
        `scénographie salle mariage 500 m² Domaine Yvelines`,
        `location accessoires éclairage décoratif mariage 130 invités Île-de-France`,
        `décoration table particulers semi-intime 60 invités Vincennes`,
        `décoration centre de table audacieux évènement privé Paris 12ᵉ`,
        `agence décoration fête d'anniversaire adulte 100 invités Haussmann Paris`,
        `location mobilier banquet mariage 130 invités Rueil-Malmaison`,
        `décor table d'honneur mariage luxe 12 personnes Neuilly-sur-Seine`,
        `location décoration évènement anniversaire 70 invités Aubervilliers`,
        `agence évènementielle mariage privé château Yvelines`,
        `décor centre de table sur-mesure mariage Paris 9ᵉ`,
        `agence organisation soirée cocktail 80 invités Boulogne-Billancourt`,
        `décoration table thème bohème chic mariage 130 invités Seine-Saint-Denis`,
        `location mobilier design évènement mariage 11 tables Saint-Germain-en-Laye`,
        `décoration table d'honneur célébration privée luxe Paris 3ᵉ`,
        `location accessoires décoratifs mariage rustique chic 130 invités Yvelines`,
        `décor centre de table premium entreprise 120 invités Boulogne-Billancourt`,
        `décoration table invités mariage éco-responsable Paris 5ᵉ`,
        `location décor végétation suspendue évènement privé 90 invités Vincennes`,
        `agence évènementielle réception rooftop Paris 9ᵉ`,
        `location accessoires photobooth soirée mariage 140 invités Versailles`,
        `décoration table thème hiver mariage 130 invités Paris 17ᵉ`,
        `location décor féérique soirée d'anniversaire adulte 50 invités Boulogne-Billancourt`,
        `décor centre de table haut luxe mariage 130 invités Yvelines`,
        `location décor lumineux LED mariage 130 invités Saint-Germain-en-Laye`,
        `décoration table style minimaliste élégant mariage 11 tables Paris 11ᵉ`,
        `location mobilier transparent cristal mariage 130 invités Vincennes`,
        `décoration table d'honneur mariage vintage luxe Paris 16ᵉ`,
        `location machine à pop-corn mariage 130 invités Seine-Saint-Denis`,
        `décoration table thème tropical mariage 130 invités Nanterre`,
        `décoration table invités mariage champêtre chic 130 invités Île-de-France`,
        `location accessoires décoratifs célébration privée luxe 40 invités Paris 10ᵉ`,
        `décor centre de table lumineux mariage 130 invités Paris 9ᵉ`,
        `agence organisation soirée rooftop luxe 90 invités Paris 7ᵉ`,
        `location machine à barbe à papa anniversaire thème adulte 60 invités Saint-Denis`,
        `décorations table mariage élégant noir et or 11 tables Neuilly-sur-Seine`,
        `agence évènementielle fête privée intime 50 invités Paris 12ᵉ`,
        `location mobilier hélicoïdal table évènement 130 invités Versailles`,
        `décoration table d'honneur mariage satin ivoire 12 personnes Paris 6ᵉ`,
        `décoration table invités mariage luxe satin velours 130 invités Yvelines`,
        `location décor suspension florale mariage 130 invités Île-de-France`,
        `décor centre de table style industriel chic entreprise 120 invités Issy-les-Moulineaux`,
        `location accessoires décoratifs thème cinéma anniversaire 70 invités Vincennes`
      ] : [
        `agence évènementielle pour séminaire entreprise Paris`,
        `organisation lancement de produit clé en main Île-de-France`,
        `location matériel son lumière évènement professionnel Paris`,
        `évènement corporate haut de gamme Île-de-France`,
        `organisation de conférence entreprise Paris`,
        `soirée clients agence évènementielle Île-de-France`,
        `team building luxe Paris région`,
        `location mobilier évènementiel salon professionnel Paris`,
        `prestataire évènementiel B2B Île-de-France`,
        `organisation repas d'entreprise haut de gamme Paris`,
        `décor évènementiel professionnel design Île-de-France`,
        `agence évènement pour inauguration entreprise Paris`,
        `coordination évènement corporate premium Île-de-France`,
        `location infrastructure évènementielle entreprise Paris`,
        `service clé en main évènement pro Île-de-France`,
        `agence spécialisée évènements professionnels hauts budgets Paris`,
        `décor tables gala entreprise Île-de-France`,
        `évènement institutionnel design Paris`,
        `animation soirée corporate de prestige Île-de-France`,
        `organisation d'évènement bureaux & entreprise Paris`,
        // Nouveaux mots-clés longue traîne ultra-ciblés professionnels
        `agence évènementielle séminaire 60 personnes La Défense`,
        `mobilisation matériel son lumière soirée entreprise 200 personnes Boulogne-Billancourt`,
        `location mobilier salon professionnel Paris Porte de Versailles`,
        `agence évènement corporate inauguration entreprise 150 invités Île-de-France`,
        `décorations florales haut de gamme repas d'entreprise 90 invités Boulogne-Billancourt`,
        `organisation soirée clients 120 invités La Défense`,
        `agence évènementielle team building luxe 80 participants Île-de-France`,
        `location housses de chaise velours soirée entreprise 160 invités Neuilly-sur-Seine`,
        `agence évènementielle location mobilier coworking évènement Paris`,
        `location pop-corn machine soirée entreprise 200 invités Boulogne-Billancourt`,
        `organisation retraite d'entreprise séminaire 30 personnes Forêt de Fontainebleau`,
        `location décor végétal luxe soirée entreprise 120 invités Issy-les-Moulineaux`,
        `location machine à pop-corn anniversaire entreprise 150 invités Paris 16ᵉ`,
        `location décor lumineux dîner assis 140 invités Nanterre`,
        `agence évènementielle évènement pro multi-sites Île-de-France`,
        `agence organisation évènement hybride digital + live Paris`,
        `location matériel technique conférence 200 personnes Porte de Versailles`,
        `agence évènementielle soirée networking 100 invités Paris 1ᵉʳ`,
        `location mobilier lounge évènement entreprise 130 invités Saint-Denis`,
        `agence organisation séminaire team building outdoor Île-de-France`,
        `agence évènementielle lancement produit luxe Paris 8ᵉ`,
        `location matériel évènementiel écoconçu Île-de-France`,
        `agence décoration table VIP entreprises 120 invités Issy-les-Moulineaux`,
        `agence organisation fête anniversaire corporate 120 invités Boulogne-Billancourt`,
        `agence évènementielle soirée caritative gala Paris 8ᵉ`,
        `location décor végétation luxuriante dîner entreprise 130 invités Neuilly-sur-Seine`,
        `agence organisation repas d'entreprise haut de gamme 150 invités Yvelines`,
        `agence évènementielle coordination logistique évènement pro 200 invités Paris`,
        `décoration table invités entreprise réception 100 invités La Défense`,
        `location mobilier bar à cocktails soirée entreprise 140 invités Boulogne-Billancourt`,
        `agence évènementielle team building luxe hybride 80 invités Boulogne-Billancourt`,
        `agence organisation séminaire digital hybrid Paris 16ᵉ`
      ];
      
      // Mots-clés mixtes communs à tous les évènements
      const mixedKeywords = [
        `location matériel évènementiel luxe Île-de-France`,
        `location décoration évènementielle premium Paris`,
        `décorations de table personnalisées évènement Île-de-France`,
        `offre location mobilier pour mariage ou entreprise Paris`,
        `service décoration et scénographie évènementielle Île-de-France`,
        `décor sur mesure pour évènement haut de gamme Paris`,
        `installation évènementielle clé en main Île-de-France`,
        `prestataire décoration table invités & mariés Paris`,
        `service décoration entièrement inclus évènement privé/professionnel Île-de-France`,
        `agence évènementielle design et luxe Paris Île-de-France`
      ];
      
      const keywords = [
        `${event.title} Paris`,
        `${event.title} Île-de-France`,
        `organisation ${event.title} maison eridia`,
        `${event.title} eridia`,
        `${event.title} sur mesure Paris`,
        `personnalisation ${event.title} Île-de-France`,
        `organisateur ${event.title} Paris`,
        `agence ${event.title} Paris`,
        `${event.title} clé en main Paris`,
        `évènement ${event.title} Île-de-France`,
        `${event.title} ${eventType} Paris`,
        `planification ${event.title} maison eridia`,
        `${event.title} haut de gamme Paris`,
        `coordination ${event.title} Île-de-France`,
        `devis ${event.title} gratuit`,
        `maison eridia ${event.title}`,
        event.title,
        ...longTailKeywords,
        ...mixedKeywords
      ].join(', ');

      // Créer un titre SEO optimisé pour le CTR et le ranking
      const seoTitle = `${event.title} Paris & Île-de-France | Organisation Clé en Main — Maison Eridia`;

      // Créer une description optimisée pour le CTR avec call-to-action
      const baseDescription = event.description || `Organisez votre ${event.title.toLowerCase()} parfait en Île-de-France`;
      const metaDescription = `✨ ${event.title} sur mesure par Maison Eridia à Paris. ${baseDescription.substring(0, 80)}. 🎯 Devis gratuit 24h • Organisation clé en main • À partir de ${event.base_price}€`.substring(0, 160);

      // Structured data enrichies avec Breadcrumbs, Service et FAQ pour rich snippets
      const structuredData = [
        // Schema Service principal
        {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": `Organisation ${event.title} Paris`,
          "alternateName": `${event.title} Maison Eridia`,
          "description": event.description || `Service d'organisation de ${event.title} en Île-de-France par Maison Eridia. Organisation complète clé en main.`,
          "image": event.image_url || "https://maisoneridia.fr/lovable-uploads/maison-eridia-logo.png",
          "provider": {
            "@type": "LocalBusiness",
            "name": "Maison Eridia",
            "url": "https://maisoneridia.fr",
            "logo": "https://maisoneridia.fr/lovable-uploads/maison-eridia-logo.png",
            "telephone": "+33749403460",
            "email": "hello@maisoneridia.com",
            // Écrit en base (seo_metadata.structured_data) puis servi tel quel : une
            // correction ici ne vaut que pour les synchronisations SUIVANTES. Les lignes
            // déjà enregistrées ont été réalignées par la migration
            // 20261103120000_seo_metadata_adresse_entreprise.sql.
            "address": ADRESSE_ENTREPRISE,
            "geo": GEO_ENTREPRISE,
            "priceRange": "€€€"
          },
          "serviceType": `Organisation ${event.title}`,
          "category": `Évènementiel ${eventType}`,
          "areaServed": [
            {
              "@type": "City",
              "name": "Paris"
            },
            {
              "@type": "State",
              "name": "Île-de-France"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Hauts-de-Seine"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Val-de-Marne"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Seine-Saint-Denis"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Yvelines"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Essonne"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Val-d'Oise"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Seine-et-Marne"
            }
          ],
          "offers": {
            "@type": "Offer",
            "price": event.base_price?.toString() || "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            "url": `https://maisoneridia.fr${pagePath}`,
            "itemOffered": {
              "@type": "Service",
              "name": event.title
            },
            "seller": {
              "@type": "Organization",
              "name": "Maison Eridia"
            }
          },
          // Annonçait 50 avis ; la fiche Google en compte 20.
          "aggregateRating": NOTE_AGREGEE_GOOGLE,
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `Services ${event.title}`,
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Organisation complète"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Décoration personnalisée"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Coordination le jour J"
                }
              }
            ]
          }
        },
        // Breadcrumbs pour améliorer la navigation
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": event.type_categorie === 'professionnel' ? [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": "https://maisoneridia.fr/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Professionnels",
              "item": "https://maisoneridia.fr/professionnels"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": event.title,
              "item": `https://maisoneridia.fr${pagePath}`
            }
          ] : [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": "https://maisoneridia.fr/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Évènements",
              "item": "https://maisoneridia.fr/evenements"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": event.title,
              "item": `https://maisoneridia.fr${pagePath}`
            }
          ]
        },
        // FAQ Schema pour rich snippets dans Google
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": `Quel est le prix pour un ${event.title} ?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `L'organisation d'un ${event.title} avec Maison Eridia commence à partir de ${event.base_price}€. Le tarif final dépend de vos prestations choisies, du nombre d'invités et de vos options de personnalisation. Demandez votre devis gratuit en ligne.`
              }
            },
            {
              "@type": "Question",
              "name": `Quelles zones Maison Eridia couvre-t-elle pour un ${event.title} ?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Maison Eridia intervient sur toute l'Île-de-France : Paris, Hauts-de-Seine, Seine-Saint-Denis, Val-de-Marne, Yvelines, Essonne, Val-d'Oise et Seine-et-Marne."
              }
            },
            {
              "@type": "Question",
              "name": `Peut-on personnaliser entièrement son ${event.title} ?`,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": `Oui, Maison Eridia propose une personnalisation complète de votre ${event.title} : choix du thème, décoration sur mesure, prestations sélectionnées selon vos envies, et coordination clé en main. Chaque détail est pensé pour créer un évènement unique.`
              }
            },
            {
              "@type": "Question",
              "name": "Comment obtenir un devis pour mon évènement ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Obtenez votre devis gratuit en ligne en personnalisant votre évènement sur notre site. Vous recevrez une estimation détaillée sous 24h. Le devis est sans engagement et vous permet de visualiser toutes les options disponibles."
              }
            }
          ]
        }
      ];

      // Mots-clés locaux enrichis avec longue traîne géolocalisée
      const localKeywordsArray = [
        `${event.title} Paris`,
        `${event.title} Île-de-France`,
        `organisation ${event.title} 75`,
        `${event.title} sur mesure Paris`,
        `maison eridia ${event.title}`,
        `eridia ${event.title}`,
        `agence ${event.title} Paris`,
        `organisateur ${event.title} Île-de-France`,
        `${event.title} Hauts-de-Seine 92`,
        `${event.title} Val-de-Marne 94`,
        `${event.title} Seine-Saint-Denis 93`,
        `${event.title} Yvelines 78`,
        `${event.title} Essonne 91`,
        `${event.title} Val-d'Oise 95`,
        `${event.title} Seine-et-Marne 77`,
        `${event.title} région parisienne`,
        `décoration ${event.title} Paris Île-de-France`,
        `location matériel ${event.title} Paris`,
        `prestataire ${event.title} haut de gamme Île-de-France`,
        `coordination ${event.title} clé en main Paris`
      ];

      return {
        page_path: pagePath,
        title: seoTitle,
        meta_description: metaDescription,
        keywords,
        og_image_url: event.image_url || "https://maisoneridia.fr/lovable-uploads/ea8eac45-2437-49ee-9f9d-9d8fc508f0b0.png",
        no_index: false,
        page_type: event.type_categorie as 'particulier' | 'professionnel', // Ajout du type de page pour filtrage
        canonical_url: `https://maisoneridia.fr${pagePath}`,
        structured_data: structuredData,
        target_zones: undefined as string[] | undefined,
        local_keywords: localKeywordsArray.join(', '),
        geo_targeting: {
          primary_zone: 'Île-de-France',
          secondary_zones: ['Paris', 'Hauts-de-Seine', 'Seine-Saint-Denis', 'Val-de-Marne', 'Essonne', 'Yvelines', 'Val-d\'Oise', 'Seine-et-Marne'],
          service_area: [
            {
              name: 'Paris',
              type: 'City',
              geo_coordinates: {
                latitude: 48.8566,
                longitude: 2.3522
              }
            },
            {
              name: 'Île-de-France',
              type: 'Region'
            }
          ]
        },
        hreflang_tags: {
          'fr-FR': `https://maisoneridia.fr${pagePath}`,
          'x-default': `https://maisoneridia.fr${pagePath}`
        }
      };
    });

    // Utiliser upsert pour créer ou mettre à jour les métadonnées
    const { error: upsertError } = await supabase
      .from('seo_metadata')
      .upsert(seoMetadataList, {
        onConflict: 'page_path',
        ignoreDuplicates: false // Mettre à jour si déjà existant
      });

    if (upsertError) {
      console.error('Erreur lors de la synchronisation SEO:', upsertError);
      throw upsertError;
    }

    console.log(`✅ ${seoMetadataList.length} pages d'évènements synchronisées avec succès`);
    return seoMetadataList.length;
  } catch (error) {
    console.error('Erreur dans syncEventCategoriesToSeo:', error);
    throw error;
  }
};

/**
 * Nettoie les métadonnées SEO pour les évènements qui n'existent plus ou sont inactifs
 */
export const cleanupInactiveEventSeoMetadata = async () => {
  try {
    console.log('Nettoyage des métadonnées SEO d\'évènements inactifs...');

    // Récupérer toutes les métadonnées SEO d'évènements (particuliers et professionnels)
    const { data: seoPages, error: seoError } = await supabase
      .from('seo_metadata')
      .select('page_path')
      .or('page_path.like./evenements/%/personnaliser,page_path.like./professionnels/evenements/%');

    if (seoError) throw seoError;

    if (!seoPages || seoPages.length === 0) {
      console.log('Aucune page SEO d\'évènement à vérifier');
      return;
    }

    // Récupérer tous les évènements actifs avec leurs slugs et IDs
    const { data: activeEvents, error: eventsError } = await supabase
      .from('event_categories')
      .select('id, slug, type_categorie')
      .eq('is_active', true);

    if (eventsError) throw eventsError;

    const activeEventIds = new Set(activeEvents?.map(e => e.id) || []);
    const activeEventSlugs = new Set(activeEvents?.filter(e => e.type_categorie === 'professionnel').map(e => e.slug) || []);

    // Identifier les pages SEO à supprimer
    const pagesToDelete = seoPages.filter(page => {
      if (page.page_path.includes('/professionnels/evenements/')) {
        // Pour les évènements professionnels, vérifier par slug
        const slug = page.page_path.split('/professionnels/evenements/')[1];
        return !activeEventSlugs.has(slug);
      } else {
        // Pour les évènements particuliers, extraire l'UUID
        const pathWithoutSuffix = page.page_path.replace('/personnaliser', '');
        const eventId = pathWithoutSuffix.slice(-36);
        return !activeEventIds.has(eventId);
      }
    });

    if (pagesToDelete.length > 0) {
      const pathsToDelete = pagesToDelete.map(p => p.page_path);
      
      const { error: deleteError } = await supabase
        .from('seo_metadata')
        .delete()
        .in('page_path', pathsToDelete);

      if (deleteError) throw deleteError;

      console.log(`🗑️ ${pagesToDelete.length} pages SEO d'évènements inactifs supprimées`);
    } else {
      console.log('Aucune page SEO d\'évènement à supprimer');
    }

    return pagesToDelete.length;
  } catch (error) {
    console.error('Erreur dans cleanupInactiveEventSeoMetadata:', error);
    throw error;
  }
};
